
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  ATTRIBUTE_SOURCE_ATTRIBUTE,
  ATTRIBUTE_SOURCE_NAME_ID,
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_EXTERNAL_SUBJECT,
  CANONICAL_FIELD_FIRST_NAME,
  CANONICAL_FIELD_LAST_NAME,
  IApiFieldError,
  identityIntegrationsService,
  IDENTITY_INTEGRATION_PROTOCOL_OIDC,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
  IIdentityAttributeMapping,
  IIdentityIntegration,
  isMutableSubjectClaim,
  isSubjectSource,
  OIDC_EMAIL_CLAIM,
  subjectSourceFor,
} from '@/services/identityIntegrationsService';

const EXTERNAL_SUBJECT_CHANGE_REJECTED = 'externalSubjectMappingChangeRejected';

const OPTIONAL_FIELDS = [
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_FIRST_NAME,
  CANONICAL_FIELD_LAST_NAME,
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
];

/**
 * The mapping boundary, as a form: canonical Smart Point fields on the left,
 * whatever this IdP calls them on the right. No field here is named after a
 * provider — that is exactly what the right-hand column is for.
 */
@Component({ name: 'AttributeMappingCard' })
export default class AttributeMappingCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected rows: { [field: string]: string | null } = {};
  protected externalSubjectSource: string = ATTRIBUTE_SOURCE_NAME_ID;
  protected busy: boolean = false;
  protected confirmingExternalSubject: boolean = false;
  protected errors: IApiFieldError[] = [];

  protected readonly optionalFields = OPTIONAL_FIELDS;
  protected readonly emailField = CANONICAL_FIELD_EMAIL;

  created() {
    this.reset();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.reset();
  }

  get isOidc(): boolean {
    return this.integration.protocol === IDENTITY_INTEGRATION_PROTOCOL_OIDC;
  }

  /**
   * The protocol's own subject — the SAML NameID or the OIDC `sub` claim —
   * spelled the way new configuration for this protocol is written.
   */
  get subjectSource(): string {
    return subjectSourceFor(this.integration.protocol);
  }

  get subjectSelected(): boolean {
    return isSubjectSource(this.externalSubjectSource);
  }

  /**
   * The same form speaks of SAML attributes or of ID Token claims depending on
   * the protocol; the configuration underneath is identical.
   */
  text(key: string): string {
    return 'identityIntegrations.attributeMapping.' + (this.isOidc ? 'oidc.' : '') + key;
  }

  get isAttributeMapped(): boolean {
    return this.integration.community_resolution.mode === COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;
  }

  /**
   * Receded, never hidden and never locked.
   *
   * Switching resolution to attribute mapping requires this field to already
   * be mapped, so hiding it under `fixed` — or making it read-only — left no
   * way in at all: the field could not be filled because the mode was fixed,
   * and the mode could not be changed because the field was empty. It has to
   * stay fillable ahead of the switch, which is the order an operator actually
   * works in.
   */
  isUnused(field: string): boolean {
    return field === CANONICAL_FIELD_COMMUNITY_REFERENCE && !this.isAttributeMapped;
  }

  /**
   * Attribute-mapped resolution cannot resolve anything without the reference,
   * so an empty one is always worth flagging.
   */
  get missingCommunityReference(): boolean {
    return this.isAttributeMapped && !this.trimmed(CANONICAL_FIELD_COMMUNITY_REFERENCE);
  }

  /**
   * Mirrors the backend rule exactly: an ACTIVE integration may not be left in
   * a state that fails every login, an inactive one may be edited freely and is
   * re-checked on enable. Blocking the button here only saves a round trip to a
   * rejection the server was always going to issue — it is not a second,
   * independent rule.
   */
  get communityReferenceBlocksSave(): boolean {
    return this.missingCommunityReference && this.integration.status === IDENTITY_INTEGRATION_STATUS_ACTIVE;
  }

  get blocksSave(): boolean {
    return this.communityReferenceBlocksSave || this.missingExternalSubjectName || this.mutableExternalSubjectClaim;
  }

  /**
   * OIDC only: a subject read from an address or a username moves with it,
   * so whoever gets that value next would sign in as the linked user.
   */
  get mutableExternalSubjectClaim(): boolean {
    return this.isOidc && !this.subjectSelected && isMutableSubjectClaim(this.trimmed(CANONICAL_FIELD_EXTERNAL_SUBJECT));
  }

  get externalSubjectErrors(): string[] {
    const errors = this.fieldError(CANONICAL_FIELD_EXTERNAL_SUBJECT);

    return this.mutableExternalSubjectClaim
      ? [this.$t('identityIntegrations.errors.mutableExternalSubjectClaim') as string].concat(errors)
      : errors;
  }

  /**
   * "Read it from a named claim" without a name would be saved as no mapping
   * at all — which means the subject — so the form would say one thing and
   * the login do another.
   */
  get missingExternalSubjectName(): boolean {
    return !this.subjectSelected && !this.trimmed(CANONICAL_FIELD_EXTERNAL_SUBJECT);
  }

  get emailUnverifiable(): boolean {
    const email = this.trimmed(CANONICAL_FIELD_EMAIL);

    return this.isOidc && email !== null && email !== OIDC_EMAIL_CLAIM;
  }

  fieldHint(field: string): string {
    if (this.isOidc && field === CANONICAL_FIELD_EMAIL) {
      return this.$t('identityIntegrations.attributeMapping.oidc.emailHint') as string;
    }

    return this.$t('identityIntegrations.canonicalFieldHints.' + field) as string;
  }

  get sourceItems() {
    return [
      { text: this.$t(this.text('sources.name_id')), value: this.subjectSource },
      { text: this.$t(this.text('sources.attribute')), value: ATTRIBUTE_SOURCE_ATTRIBUTE },
    ];
  }

  /**
   * Changing this rewrites the lookup key of every existing link, so warn as
   * soon as the form diverges — not only once the save has been rejected.
   */
  get showExternalSubjectWarning(): boolean {
    return this.integration.linked_identity_count > 0 && this.externalSubjectChanged;
  }

  /**
   * Null whenever the subject comes from the NameID, whatever the form still
   * holds. A NameID mapping has no attribute name by definition, so a value
   * left over from a previous choice must not count as configuration — it
   * would otherwise mark the form dirty and fire the "you are changing the
   * external subject" warning when nothing actually changed.
   */
  get externalSubjectName(): string | null {
    return this.subjectSelected ? null : this.trimmed(CANONICAL_FIELD_EXTERNAL_SUBJECT);
  }

  // EN: `name_id` and `subject` are the same source, so a stored `name_id`
  //     shown as "subject" is not a change.
  // ES: `name_id` y `subject` son el mismo origen, así que un `name_id`
  //     guardado mostrado como "subject" no es un cambio.
  get externalSubjectChanged(): boolean {
    const current = this.mappingFor(CANONICAL_FIELD_EXTERNAL_SUBJECT);
    const currentIsSubject = !current || isSubjectSource(current.source_type);
    const currentName = current && current.source_type === ATTRIBUTE_SOURCE_ATTRIBUTE ? current.source_name : null;

    return currentIsSubject !== this.subjectSelected || currentName !== this.externalSubjectName;
  }

  get dirty(): boolean {
    if (this.externalSubjectChanged) {
      return true;
    }

    return OPTIONAL_FIELDS.some((field) => {
      const current = this.mappingFor(field);
      return (current ? current.source_name : null) !== this.trimmed(field);
    });
  }

  // The deliberate-override refusal opens the dialog instead; anything else
  // reported against `attributes` as a whole belongs in the general alert.
  get generalError(): string | null {
    return generalErrorMessage(this, this.errors.filter((each) => each.title !== EXTERNAL_SUBJECT_CHANGE_REJECTED), this.errorRefs());
  }

  fieldError(field: string): string[] {
    const index = this.payload().findIndex((mapping) => mapping.canonical_field === field);

    if (index === -1) {
      return [];
    }

    return fieldErrorMessages(this, this.errors, 'attributes.' + index + '.source_name').concat(
      fieldErrorMessages(this, this.errors, 'attributes.' + index + '.canonical_field')
    );
  }

  reset() {
    const external = this.mappingFor(CANONICAL_FIELD_EXTERNAL_SUBJECT);

    this.externalSubjectSource = external && !isSubjectSource(external.source_type)
      ? ATTRIBUTE_SOURCE_ATTRIBUTE
      : this.subjectSource;
    this.rows = { [CANONICAL_FIELD_EXTERNAL_SUBJECT]: external ? external.source_name : null };

    OPTIONAL_FIELDS.forEach((field) => {
      const mapping = this.mappingFor(field);
      this.$set(this.rows, field, mapping ? mapping.source_name : null);
    });

    this.errors = [];
    this.confirmingExternalSubject = false;
  }

  async submit(confirmed: boolean = false) {
    if (!confirmed && this.showExternalSubjectWarning) {
      this.confirmingExternalSubject = true;
      return;
    }

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.replaceAttributeMappings(
        this.integration.id,
        this.payload(),
        confirmed
      );
      this.confirmingExternalSubject = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
      // Someone may have signed in since this page loaded: the server then
      // refuses a change the form did not know to confirm. Ask, then resend.
      this.confirmingExternalSubject = !confirmed && this.errors.some((each) => each.title === EXTERNAL_SUBJECT_CHANGE_REJECTED);
    } finally {
      this.busy = false;
    }
  }

  /**
   * An unmapped field is an absent row, never a row with an empty name: the
   * backend treats "no mapping" as a valid fail-closed state and an empty
   * attribute name as a configuration error.
   */
  private payload(): IIdentityAttributeMapping[] {
    const mappings: IIdentityAttributeMapping[] = [];
    const externalSubjectName = this.externalSubjectName;

    if (this.subjectSelected) {
      mappings.push({
        canonical_field: CANONICAL_FIELD_EXTERNAL_SUBJECT,
        source_type: this.subjectSource,
        source_name: null,
        required: true,
      });
    } else if (externalSubjectName) {
      mappings.push({
        canonical_field: CANONICAL_FIELD_EXTERNAL_SUBJECT,
        source_type: ATTRIBUTE_SOURCE_ATTRIBUTE,
        source_name: externalSubjectName,
        required: true,
      });
    }

    OPTIONAL_FIELDS.forEach((field) => {
      const name = this.trimmed(field);

      if (name) {
        mappings.push({
          canonical_field: field,
          source_type: ATTRIBUTE_SOURCE_ATTRIBUTE,
          source_name: name,
          required: false,
        });
      }
    });

    return mappings;
  }

  private errorRefs(): string[] {
    return this.payload().map((mapping, index) => 'attributes.' + index + '.source_name');
  }

  trimmed(field: string): string | null {
    const value = (this.rows[field] || '').trim();
    return value === '' ? null : value;
  }

  private mappingFor(field: string): IIdentityAttributeMapping | null {
    const attributes = this.integration.identity_mapping.attributes || [];
    const found = attributes.filter((mapping) => mapping.canonical_field === field);

    return found.length ? found[0] : null;
  }
}
